import { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Tag,
  Switch,
  IconButton,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Circle,
  VStack,
  HStack,
  SimpleGrid,
  Divider,
  Spinner,
  Alert,
  AlertIcon,
  Image,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { MdClose, MdDelete, MdVisibility, MdSearch } from 'react-icons/md';
import StrongInput from '../components/common/StrongInput';
import Page from '../components/common/Page';
import { ApiCommand } from '../lib/Api';
import useToastNotification from '../lib/hooks/useToastNotification';
import StrongTable from '../components/common/StrongTable';
import StrongPagination from '../components/common/StrongPagination';
import TempPasswordPanel from '../components/users/TempPasswordPanel';
import useApi from '../lib/hooks/useApi';
import urlConstants from '../lib/constants/url.constants';

const USER_COLUMNS = [
  {
    key: 'username',
    label: 'Username',
  },
  {
    key: 'profilePicture',
    label: 'Profile Picture',
    render: (value: any) => {
      if (value!== null) {
        return <img src={value} alt='Profile' style={{ width: '40px', height: '40px', borderRadius: '50%' }} />;
      } else {
        return <img src='/favicon.png' alt='Profile' style={{ width: '40px', height: '40px', borderRadius: '50%' }} />;
      }
    },
    
  },
  {
    key: 'firstName',
    label: 'First Name',
  },
  {
    key: 'lastName',
    label: 'Last Name', 
  },
  {
    key: 'dob',
    label: 'Date of Birth',
    render: (value: any) => {
      if (value) {
        const date = new Date(value);
        return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
      }
      return '-';
    },
  },
  {
    key: 'email',
    label: 'Email',
  },
  {
    key: 'role',
    label: 'Role',
    render: (value: any) => (
      <Tag size='sm' colorScheme={value === 1 ? 'blue' : 'green'}>
        {value === 1 ? 'Admin' : 'User'}
      </Tag>
    ),
  },
  {
    key: 'active',
    label: 'Status',
    render: (value: any) => <Switch colorScheme='primary' isChecked={value} />,
  },
];

const { getUsers: getUsersUrl, deleteUser: deleteUserUrl } = urlConstants.users;
const backendBaseUrl = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, '');
const backendUrl = (path: string) => `${backendBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;

type ProgramRef = {
  _id?: string;
  title?: string;
  subscriptionType?: string;
};

type PurchasedProgram = {
  programId?: ProgramRef | string | null;
  purchasedType?: string;
  status?: string;
  subscriptionType?: string;
  source?: string;
  startDate?: string;
  endDate?: string;
  _id?: string;
};

type WorkoutSummary = {
  totalEntries?: number;
  completedEntries?: number;
  skippedEntries?: number;
  inProgressEntries?: number;
  exerciseEntries?: number;
  restEntries?: number;
  totalSets?: number;
  lastActivityAt?: string;
};

type RecentWorkout = {
  _id?: string;
  programId?: ProgramRef | string | null;
  weekIndex?: number;
  dayIndex?: number;
  dayType?: string;
  sourceType?: string;
  exerciseId?: ProgramRef | string | null;
  restDayId?: ProgramRef | string | null;
  swappedExerciseId?: ProgramRef | string | null;
  startedAt?: string;
  finishedAt?: string;
  status?: string;
  sets?: any[];
  notes?: string;
  createdAt?: string;
};

type DeviceInfo = {
  phoneModel?: string | null;
  appVersion?: string | null;
  platform?: 'ios' | 'android' | null;
  osVersion?: string | null;
  lastUpdated?: string | null;
};

type UserDetails = {
  user: User & {
    uid?: number | string;
    source?: string;
    signupSource?: string;
    subscriptionType?: string;
    profilePicture?: string;
    dob?: string;
    weight?: number | string;
    height?: number | string;
    location?: string;
    country?: string;
    state?: string;
    city?: string;
    purchasedPrograms?: PurchasedProgram[];
    activeProgram?: ProgramRef | string | null;
    updatedAt?: string;
    firstLoginAt?: string;
    lastLoginAt?: string;
    measurementUnit?: 'Metric' | 'Imperial' | null;
    gender?: string | null;
    workoutsCompleted?: number | null;
    deviceInfo?: DeviceInfo;
  };
  workoutSummary?: WorkoutSummary;
  recentWorkouts?: RecentWorkout[];
};

const getUserId = (user?: User | null) => user?._id || user?.id || '';

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatShortDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getDisplayTitle = (value?: ProgramRef | string | null, fallback = '-') => {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  return value.title || fallback;
};

const programSourceLabel: Record<string, string> = {
  woocommerce: 'WooCommerce',
  admin: 'Admin',
  app: 'App',
  legacy: 'Legacy',
};

const programSourceColor: Record<string, string> = {
  woocommerce: 'purple',
  admin: 'green',
  app: 'blue',
  legacy: 'orange',
};

const getExerciseOrRestTitle = (workout: RecentWorkout) => {
  return (
    getDisplayTitle(workout.exerciseId, '') ||
    getDisplayTitle(workout.restDayId, '') ||
    getDisplayTitle(workout.swappedExerciseId, '') ||
    '-'
  );
};

const getFullName = (user?: User | null) => {
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  return fullName || user?.username || '-';
};

const Users = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortCriteria, setSortCriteria] = useState<string>('newest');
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [detailsUser, setDetailsUser] = useState<User | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [isUserDetailsLoading, setIsUserDetailsLoading] = useState<boolean>(false);
  const [userDetailsError, setUserDetailsError] = useState<string>('');

  const showToast = useToastNotification();

  const { loading: isFetchUsersLoading, sendRequest: getUsers } =
    useApi<GetUserResopnse>();
  const [isDeleteUserLoading, setIsDeleteUserLoading] = useState<boolean>(false);

  // Grant program
  const [allPrograms, setAllPrograms] = useState<{ _id: string; title: string }[]>([]);
  const [selectedGrantProgram, setSelectedGrantProgram] = useState('');
  const [grantProgramSource, setGrantProgramSource] = useState<'admin' | 'legacy'>('admin');
  const [isUpdatingPreferences, setIsUpdatingPreferences] = useState(false);
  const [isUpdatingGender, setIsUpdatingGender] = useState(false);
  const [workoutsCompletedDraft, setWorkoutsCompletedDraft] = useState('');
  const [isUpdatingWorkoutsCompleted, setIsUpdatingWorkoutsCompleted] = useState(false);
  const [removingProgramId, setRemovingProgramId] = useState('');
  const [isProgramsLoading, setIsProgramsLoading] = useState(false);
  const { loading: isGranting, sendRequest: grantProgramRequest } = useApi<any>();
  const { sendRequest: fetchAllPrograms } = useApi<any>();

  const fetchUsers = () => {
    getUsers({
      callback: (data: GetUserResopnse | null, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        if (!data) return null;
        setUsers(data.users);
        setTotalPages(data.pagination.totalPages);
        setTotalCount(data.pagination.totalCount);
      },
      command: ApiCommand.GET,
      url: getUsersUrl,
      options: {
        page: currentPage,
        limit: pageSize,
        search: searchQuery,
        sort: sortCriteria,
      },
    });
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, pageSize, searchQuery, sortCriteria]);

  const fetchProgramTitles = () => {
    if (isProgramsLoading || allPrograms.length > 0) return;
    setIsProgramsLoading(true);
    fetchAllPrograms({
      command: ApiCommand.GET,
      url: urlConstants.programInfo.getTitles,
      callback: (data: any, error: string | null) => {
        setIsProgramsLoading(false);
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        const programs = Array.isArray(data) ? data : data?.sections || [];
        setAllPrograms(programs.map((program: any) => ({
          _id: program._id,
          title: program.title,
        })));
      },
    });
  };

  useEffect(() => {
    fetchProgramTitles();
  }, []);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const openDeleteConfirmation = (user: User) => {
    setUserToDelete(user);
  };

  const closeDeleteConfirmation = () => {
    if (!isDeleteUserLoading) {
      setUserToDelete(null);
    }
  };

  const handleDeleteUser = async () => {
    const userId = getUserId(userToDelete);
    const token = localStorage.getItem('strong_auth_token');

    if (!userId) {
      showToast({
        title: 'Failed',
        description: 'User ID is missing',
        status: 'error',
      });
      return;
    }

    if (!token) {
      showToast({
        title: 'Failed',
        description: 'You are not logged in. Please log in again before deleting a user.',
        status: 'error',
      });
      return;
    }

    setIsDeleteUserLoading(true);

    try {
      const response = await fetch(`${backendUrl(deleteUserUrl)}/${userId}`, {
        method: 'DELETE',
        headers: {
          Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        showToast({
          title: 'Failed',
          description: data?.message || 'Failed to delete user',
          status: 'error',
        });
        return;
      }

      showToast({
        title: 'Success',
        description: data?.message || 'User deleted successfully',
        status: 'success',
      });
      setUserToDelete(null);
      fetchUsers();
    } catch (error: any) {
      showToast({
        title: 'Failed',
        description: error?.message || 'Failed to delete user',
        status: 'error',
      });
    } finally {
      setIsDeleteUserLoading(false);
    }
  };

  const closeUserDetails = () => {
    setDetailsUser(null);
    setUserDetails(null);
    setUserDetailsError('');
    setSelectedGrantProgram('');
    setGrantProgramSource('admin');
  };

  const handleGrantProgram = () => {
    if (!selectedGrantProgram || !detailsUser) return;
    const userId = getUserId(detailsUser);
    if (!userId) return;
    grantProgramRequest({
      command: ApiCommand.POST,
      url: urlConstants.users.grantProgram(userId),
      options: { programId: selectedGrantProgram, source: grantProgramSource },
      callback: (data, error) => {
        if (error) {
          showToast({ title: 'Failed', description: error, status: 'error' });
          return;
        }
        showToast({ title: 'Program granted', description: 'Program added successfully', status: 'success' });
        setSelectedGrantProgram('');
        setGrantProgramSource('admin');
        if (userDetails?.user && data?.purchasedPrograms) {
          setUserDetails({ ...userDetails, user: { ...userDetails.user, purchasedPrograms: data.purchasedPrograms } });
        }
      },
    });
  };

  const getPurchasedProgramId = (item: PurchasedProgram) => {
    const programId = typeof item.programId === 'object' ? item.programId?._id : item.programId;
    return item._id || programId || '';
  };

  const handleRemoveProgramAccess = async (item: PurchasedProgram) => {
    if (!detailsUser) return;
    const userId = getUserId(detailsUser);
    const purchaseId = getPurchasedProgramId(item);
    if (!userId || !purchaseId) return;

    const programTitle = getDisplayTitle(item.programId, 'this program');
    const confirmed = window.confirm(`Remove access to ${programTitle}?`);
    if (!confirmed) return;

    setRemovingProgramId(purchaseId);
    try {
      const token = localStorage.getItem('strong_auth_token');
      const response = await fetch(backendUrl(urlConstants.users.removeProgram(userId, purchaseId)), {
        method: 'DELETE',
        headers: {
          ...(token ? { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` } : {}),
        },
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        showToast({
          title: 'Failed',
          description: data?.message || 'Failed to remove program access',
          status: 'error',
        });
        return;
      }

      await openUserDetails(detailsUser);
      fetchUsers();
      showToast({
        title: 'Access removed',
        description: 'Program access removed successfully',
        status: 'success',
      });
    } catch (error: any) {
      showToast({
        title: 'Failed',
        description: error?.message || 'Failed to remove program access',
        status: 'error',
      });
    } finally {
      setRemovingProgramId('');
    }
  };

  const handleGenderChange = async (gender: string) => {
    if (!detailUser) return;
    const userId = getUserId(detailUser);
    if (!userId) return;

    setIsUpdatingGender(true);
    try {
      const token = localStorage.getItem('strong_auth_token');
      const res = await fetch(backendUrl(urlConstants.users.setGender(userId)), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ gender }),
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        showToast({ title: 'Failed', description: data?.message || 'Failed to update gender', status: 'error' });
        return;
      }

      setUserDetails((current) => current ? {
        ...current,
        user: { ...current.user, gender: data?.gender ?? gender },
      } : current);
      showToast({ title: 'Saved', description: gender ? `Gender set to ${gender}` : 'Gender cleared', status: 'success' });
    } catch (error: any) {
      showToast({ title: 'Failed', description: error?.message || 'Failed to update gender', status: 'error' });
    } finally {
      setIsUpdatingGender(false);
    }
  };

  // STRONG-129: lifetime workouts-completed counter (shown on the in-app
  // celebration screen). Server-maintained; this lets support correct it.
  const handleWorkoutsCompletedSave = async () => {
    if (!detailUser) return;
    const userId = getUserId(detailUser);
    if (!userId) return;

    const value = Number(workoutsCompletedDraft);
    if (!Number.isInteger(value) || value < 0) {
      showToast({ title: 'Invalid value', description: 'Workouts completed must be a whole number of 0 or more', status: 'error' });
      return;
    }

    setIsUpdatingWorkoutsCompleted(true);
    try {
      const token = localStorage.getItem('strong_auth_token');
      const res = await fetch(backendUrl(urlConstants.users.setWorkoutsCompleted(userId)), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ workoutsCompleted: value }),
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        showToast({ title: 'Failed', description: data?.message || 'Failed to update workouts completed', status: 'error' });
        return;
      }

      setUserDetails((current) => current ? {
        ...current,
        user: { ...current.user, workoutsCompleted: data?.workoutsCompleted ?? value },
      } : current);
      showToast({ title: 'Saved', description: `Workouts completed set to ${data?.workoutsCompleted ?? value}`, status: 'success' });
    } catch (error: any) {
      showToast({ title: 'Failed', description: error?.message || 'Failed to update workouts completed', status: 'error' });
    } finally {
      setIsUpdatingWorkoutsCompleted(false);
    }
  };

  const handleMeasurementUnitChange = async (measurementUnit: 'Metric' | 'Imperial') => {
    if (!detailUser) return;
    const userId = getUserId(detailUser);
    if (!userId) return;

    setIsUpdatingPreferences(true);
    try {
      const token = localStorage.getItem('strong_auth_token');
      const res = await fetch(`${backendUrl(deleteUserUrl)}/${userId}/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ measurementUnit }),
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        showToast({ title: 'Failed', description: data?.message || 'Failed to update measurement unit', status: 'error' });
        return;
      }

      setUserDetails((current) => current ? {
        ...current,
        user: { ...current.user, measurementUnit },
      } : current);
      showToast({ title: 'Saved', description: 'Measurement unit updated', status: 'success' });
    } catch (error: any) {
      showToast({ title: 'Failed', description: error?.message || 'Failed to update measurement unit', status: 'error' });
    } finally {
      setIsUpdatingPreferences(false);
    }
  };

  const openUserDetails = async (user: User) => {
    const userId = getUserId(user);
    const token = localStorage.getItem('strong_auth_token');

    setDetailsUser(user);
    setUserDetails(null);
    setUserDetailsError('');
    setSelectedGrantProgram('');
    fetchProgramTitles();

    if (!userId) {
      setUserDetailsError('User ID is missing');
      return;
    }

    if (!token) {
      setUserDetailsError('You are not logged in. Please log in again to view user details.');
      return;
    }

    setIsUserDetailsLoading(true);

    try {
      const response = await fetch(`${backendUrl(deleteUserUrl)}/${userId}/details`, {
        method: 'GET',
        headers: {
          Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        setUserDetailsError(data?.message || 'Failed to load user details');
        return;
      }

      setUserDetails(data);
    } catch (error: any) {
      setUserDetailsError(error?.message || 'Failed to load user details');
    } finally {
      setIsUserDetailsLoading(false);
    }
  };

  const detailUser = userDetails?.user;

  // Keep the workouts-completed draft in sync with the loaded user.
  useEffect(() => {
    setWorkoutsCompletedDraft(
      detailUser?.workoutsCompleted === null || detailUser?.workoutsCompleted === undefined
        ? ''
        : String(detailUser.workoutsCompleted)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailUser?.workoutsCompleted, userDetails?.user?._id]);
  const purchasedPrograms = detailUser?.purchasedPrograms || [];
  const workoutSummary = userDetails?.workoutSummary;
  const recentWorkouts = userDetails?.recentWorkouts || [];
  const summaryCards = [
    { label: 'Total Entries', value: workoutSummary?.totalEntries ?? 0 },
    { label: 'Completed', value: workoutSummary?.completedEntries ?? 0 },
    { label: 'Skipped', value: workoutSummary?.skippedEntries ?? 0 },
    { label: 'In Progress', value: workoutSummary?.inProgressEntries ?? 0 },
    { label: 'Exercise Days', value: workoutSummary?.exerciseEntries ?? 0 },
    { label: 'Rest Days', value: workoutSummary?.restEntries ?? 0 },
    { label: 'Total Sets', value: workoutSummary?.totalSets ?? 0 },
    { label: 'Last Activity', value: formatDate(workoutSummary?.lastActivityAt) },
  ];

  return (
    <Page>
      <Flex flexDirection='column' height='full' justifyContent='space-between'>
        <Box
          backgroundColor='white'
          borderRadius='lg'
          padding={2}
        >
          <Flex justifyContent='space-between' marginBottom={4}>
            <Box flex='1' maxW='400px'>
              <StrongInput
                placeholder='Search users...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                
                rightIcon={<MdSearch color='gray.500' />}
              />
            </Box>
            <Select
              placeholder='Sort by'
              value={sortCriteria}
              onChange={(e) => setSortCriteria(e.target.value)}
              width='20%'
            >
              <option value='a-z'>A-Z</option>
              <option value='z-a'>Z-A</option>
              <option value='newest'>Newest</option>
              <option value='oldest'>Oldest</option>
            </Select>
          </Flex>
          <StrongTable
            columns={USER_COLUMNS}
            data={users}
            loading={isFetchUsersLoading}
            actions={(row) => (
              <>
                <IconButton
                  aria-label='View User'
                  icon={<MdVisibility />}
                  variant='outline'
                  colorScheme='primary'
                  size='sm'
                  marginRight={2}
                  onClick={() => openUserDetails(row)}
                />
                <IconButton
                  aria-label='Delete User'
                  icon={<MdDelete />}
                  variant='outline'
                  colorScheme='red'
                  size='sm'
                  onClick={() => openDeleteConfirmation(row)}
                />
              </>
            )}
          />
        </Box>

        <StrongPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </Flex>

      <Modal
        isOpen={Boolean(userToDelete)}
        onClose={closeDeleteConfirmation}
        isCentered
        size='md'
      >
        <ModalOverlay bg='blackAlpha.500' backdropFilter='blur(4px)' />
        <ModalContent borderRadius='lg' overflow='hidden' boxShadow='2xl'>
          <ModalHeader bg='red.50' color='gray.800' pb={3}>
            <HStack spacing={3}>
              <Circle size='42px' bg='red.100' color='red.600'>
                <MdDelete size={22} />
              </Circle>
              <Box>
                <Text fontSize='lg' fontWeight='bold'>
                  Delete user?
                </Text>
                <Text fontSize='sm' fontWeight='normal' color='gray.600'>
                  This action cannot be undone.
                </Text>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton isDisabled={isDeleteUserLoading} />
          <ModalBody py={6}>
            <VStack align='stretch' spacing={4}>
              <Text color='gray.700'>
                Are you sure you want to permanently delete this user from the system?
              </Text>
              {userToDelete && (
                <Box bg='gray.50' border='1px solid' borderColor='gray.200' borderRadius='md' p={4}>
                  <Text fontWeight='semibold' color='gray.800'>
                    {userToDelete.username}
                  </Text>
                  <Text fontSize='sm' color='gray.500'>
                    {userToDelete.email}
                  </Text>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter bg='gray.50'>
            <Button
              variant='ghost'
              mr={3}
              onClick={closeDeleteConfirmation}
              isDisabled={isDeleteUserLoading}
            >
              Cancel
            </Button>
            <Button
              colorScheme='red'
              leftIcon={<MdDelete />}
              onClick={handleDeleteUser}
              isLoading={isDeleteUserLoading}
              loadingText='Deleting'
            >
              Delete user
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={Boolean(detailsUser)}
        onClose={closeUserDetails}
        size='6xl'
        scrollBehavior='inside'
        isCentered
      >
        <ModalOverlay bg='blackAlpha.500' backdropFilter='blur(4px)' />
        <ModalContent borderRadius='lg' overflow='hidden' boxShadow='2xl' maxH='90vh'>
          <ModalHeader bg='primary.50' color='gray.800' pb={4}>
            <HStack spacing={4}>
              <Image
                src={detailUser?.profilePicture || detailsUser?.profilePicture || '/favicon.png'}
                alt={getFullName(detailUser || detailsUser)}
                boxSize='54px'
                borderRadius='full'
                objectFit='cover'
                fallbackSrc='/favicon.png'
              />
              <Box>
                <Text fontSize='xl' fontWeight='bold'>
                  {getFullName(detailUser || detailsUser)}
                </Text>
                <Text fontSize='sm' fontWeight='normal' color='gray.600'>
                  {detailUser?.email || detailsUser?.email || '-'}
                </Text>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0} bg='gray.50'>
            {isUserDetailsLoading ? (
              <Flex align='center' justify='center' minH='300px' direction='column' gap={3}>
                <Spinner color='primary.500' size='xl' />
                <Text color='gray.600'>Loading user details...</Text>
              </Flex>
            ) : userDetailsError ? (
              <Box p={6}>
                <Alert status='error' borderRadius='md'>
                  <AlertIcon />
                  {userDetailsError}
                </Alert>
              </Box>
            ) : userDetails ? (
              <Tabs colorScheme='primary' variant='line'>
                <TabList bg='white' px={6} borderBottom='1px solid' borderColor='gray.200'>
                  <Tab fontWeight='semibold' fontSize='sm'>Overview</Tab>
                  <Tab fontWeight='semibold' fontSize='sm'>Programs</Tab>
                  <Tab fontWeight='semibold' fontSize='sm'>Workouts</Tab>
                  <Tab fontWeight='semibold' fontSize='sm'>Security</Tab>
                </TabList>

                <TabPanels>
                  {/* ── Overview ── */}
                  <TabPanel px={6} py={6}>
                    <VStack align='stretch' spacing={5}>
                      <Box bg='white' border='1px solid' borderColor='gray.200' borderRadius='md' p={5}>
                        <Text fontSize='md' fontWeight='bold' mb={4}>Profile</Text>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>Name</Text>
                            <Text fontWeight='semibold'>{getFullName(detailUser)}</Text>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>Email</Text>
                            <Text fontWeight='semibold'>{detailUser?.email || '-'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>Username</Text>
                            <Text fontWeight='semibold'>{detailUser?.username || '-'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>UID</Text>
                            <Text fontWeight='semibold'>{detailUser?.uid || '-'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>Source</Text>
                            <Text fontWeight='semibold'>{detailUser?.source || '-'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>Signup Source</Text>
                            <Text fontWeight='semibold'>{detailUser?.signupSource || '-'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>Role</Text>
                            <Tag size='sm' colorScheme={detailUser?.role === 1 ? 'blue' : 'green'}>
                              {detailUser?.role === 1 ? 'Admin' : 'User'}
                            </Tag>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>Created</Text>
                            <Text fontWeight='semibold'>{formatDate(detailUser?.createdAt)}</Text>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>First Login</Text>
                            <Text fontWeight='semibold'>{formatDate(detailUser?.firstLoginAt)}</Text>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>Last Login</Text>
                            <Text fontWeight='semibold'>{formatDate(detailUser?.lastLoginAt)}</Text>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>Measurement Unit</Text>
                            <Select
                              size='sm'
                              mt={1}
                              value={detailUser?.measurementUnit || ''}
                              placeholder='Not set'
                              maxW='180px'
                              isDisabled={isUpdatingPreferences}
                              onChange={(e) => {
                                const unit = e.target.value;
                                if (unit === 'Metric' || unit === 'Imperial') {
                                  handleMeasurementUnitChange(unit);
                                }
                              }}
                            >
                              <option value='Metric'>Metric</option>
                              <option value='Imperial'>Imperial</option>
                            </Select>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>Gender</Text>
                            <Select
                              size='sm'
                              mt={1}
                              value={detailUser?.gender || ''}
                              placeholder='Not set'
                              maxW='180px'
                              isDisabled={isUpdatingGender}
                              onChange={(e) => handleGenderChange(e.target.value)}
                            >
                              <option value='Male'>Male</option>
                              <option value='Female'>Female</option>
                              <option value='Other'>Other</option>
                              <option value=''>Clear (not set)</option>
                            </Select>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>Workouts Completed</Text>
                            <HStack mt={1} maxW='220px'>
                              <StrongInput
                                size='sm'
                                type='number'
                                min={0}
                                value={workoutsCompletedDraft}
                                placeholder='Not set'
                                isDisabled={isUpdatingWorkoutsCompleted}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWorkoutsCompletedDraft(e.target.value)}
                              />
                              <Button
                                size='sm'
                                isLoading={isUpdatingWorkoutsCompleted}
                                isDisabled={
                                  workoutsCompletedDraft === '' ||
                                  Number(workoutsCompletedDraft) === detailUser?.workoutsCompleted
                                }
                                onClick={handleWorkoutsCompletedSave}
                              >
                                Save
                              </Button>
                            </HStack>
                            <Text fontSize='xs' color='gray.400' mt={1}>
                              Lifetime count shown on the in-app celebration screen
                            </Text>
                          </Box>
                        </SimpleGrid>
                      </Box>

                      <Box bg='white' border='1px solid' borderColor='gray.200' borderRadius='md' p={5}>
                        <Text fontSize='md' fontWeight='bold' mb={4}>Device Info</Text>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>Platform</Text>
                            <Text fontWeight='semibold' textTransform='capitalize'>{detailUser?.deviceInfo?.platform || '-'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>Phone Model</Text>
                            <Text fontWeight='semibold'>{detailUser?.deviceInfo?.phoneModel || '-'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>OS Version</Text>
                            <Text fontWeight='semibold'>{detailUser?.deviceInfo?.osVersion || '-'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>App Version</Text>
                            <Text fontWeight='semibold'>{detailUser?.deviceInfo?.appVersion || '-'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize='xs' color='gray.500' textTransform='uppercase'>Last Seen</Text>
                            <Text fontWeight='semibold'>{formatDate(detailUser?.deviceInfo?.lastUpdated ?? undefined)}</Text>
                          </Box>
                        </SimpleGrid>
                      </Box>
                    </VStack>
                  </TabPanel>

                  {/* ── Programs ── */}
                  <TabPanel px={6} py={6}>
                    <VStack align='stretch' spacing={5}>
                      <Box bg='white' border='1px solid' borderColor='gray.200' borderRadius='md' p={5}>
                        <Text fontSize='md' fontWeight='bold' mb={4}>Purchased Programs</Text>
                        {purchasedPrograms.length > 0 ? (
                          <VStack align='stretch' spacing={3}>
                            {purchasedPrograms.map((item, index) => {
                              const purchaseId = getPurchasedProgramId(item);
                              const isRemoving = removingProgramId === purchaseId;

                              return (
                              <Box key={purchaseId || index} border='1px solid' borderColor='gray.100' borderRadius='md' p={3}>
                                <Flex justify='space-between' align='start'>
                                  <Box>
                                    <Text fontWeight='semibold'>
                                      {getDisplayTitle(item.programId, 'Unknown Program')}
                                    </Text>
                                    <Text fontSize='sm' color='gray.500'>
                                      {item.purchasedType || '-'} · {formatShortDate(item.startDate)}
                                    </Text>
                                  </Box>
                                  <HStack spacing={2} flexShrink={0} ml={2}>
                                    {item.source ? (
                                      <Tag size='sm' colorScheme={programSourceColor[item.source] || 'gray'}>
                                        {programSourceLabel[item.source] || item.source}
                                      </Tag>
                                    ) : null}
                                    <IconButton
                                      aria-label='Remove program access'
                                      icon={<MdClose />}
                                      size='xs'
                                      variant='ghost'
                                      colorScheme='red'
                                      isLoading={isRemoving}
                                      onClick={() => handleRemoveProgramAccess(item)}
                                    />
                                  </HStack>
                                </Flex>
                              </Box>
                            )})}
                          </VStack>
                        ) : (
                          <Text color='gray.500'>No purchased programs found.</Text>
                        )}
                        <Divider my={4} />
                        <Text fontSize='xs' color='gray.500' textTransform='uppercase' mb={2}>Active Program</Text>
                        <Text fontWeight='semibold'>{getDisplayTitle(detailUser?.activeProgram, 'No active program')}</Text>
                      </Box>

                      <Box bg='white' border='1px solid' borderColor='gray.200' borderRadius='md' p={5}>
                        <Text fontSize='md' fontWeight='bold' mb={1}>Grant Program</Text>
                        <Text fontSize='sm' color='gray.500' mb={4}>Assign as Admin for manual access, or Legacy for migrated access.</Text>
                        <Flex gap={2} direction={{ base: 'column', md: 'row' }}>
                          <Select
                            placeholder={isProgramsLoading ? 'Loading programs...' : 'Select a program...'}
                            size='sm'
                            borderRadius='md'
                            value={selectedGrantProgram}
                            isDisabled={isProgramsLoading}
                            onChange={(e) => setSelectedGrantProgram(e.target.value)}
                          >
                            {allPrograms
                              .filter((p) => !purchasedPrograms.some((pp: any) => {
                                const id = typeof pp.programId === 'object' ? pp.programId?._id : pp.programId;
                                return id === p._id;
                              }))
                              .map((p) => (
                                <option key={p._id} value={p._id}>{p.title}</option>
                              ))}
                          </Select>
                          <Select
                            size='sm'
                            borderRadius='md'
                            value={grantProgramSource}
                            onChange={(e) => setGrantProgramSource(e.target.value as 'admin' | 'legacy')}
                            maxW={{ base: '100%', md: '150px' }}
                          >
                            <option value='admin'>Admin</option>
                            <option value='legacy'>Legacy</option>
                          </Select>
                          <Button
                            size='sm'
                            colorScheme='primary'
                            isDisabled={!selectedGrantProgram}
                            isLoading={isGranting}
                            onClick={handleGrantProgram}
                            flexShrink={0}
                          >
                            Grant
                          </Button>
                        </Flex>
                      </Box>
                    </VStack>
                  </TabPanel>

                  {/* ── Workouts ── */}
                  <TabPanel px={6} py={6}>
                    <VStack align='stretch' spacing={5}>
                      <Box bg='white' border='1px solid' borderColor='gray.200' borderRadius='md' p={5}>
                        <Text fontSize='md' fontWeight='bold' mb={4}>Workout Summary</Text>
                        <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={3}>
                          {summaryCards.map((item) => (
                            <Box key={item.label} bg='gray.50' border='1px solid' borderColor='gray.100' borderRadius='md' p={4}>
                              <Text fontSize='xs' color='gray.500' textTransform='uppercase'>{item.label}</Text>
                              <Text fontSize='xl' fontWeight='bold' color='gray.800'>{item.value}</Text>
                            </Box>
                          ))}
                        </SimpleGrid>
                      </Box>

                      <Box bg='white' border='1px solid' borderColor='gray.200' borderRadius='md' p={5}>
                        <Text fontSize='md' fontWeight='bold' mb={4}>Recent Workouts</Text>
                        {recentWorkouts.length > 0 ? (
                          <Box overflowX='auto'>
                            <Table size='sm' variant='simple'>
                              <Thead>
                                <Tr>
                                  <Th>Program</Th>
                                  <Th>Week</Th>
                                  <Th>Day</Th>
                                  <Th>Type</Th>
                                  <Th>Status</Th>
                                  <Th>Exercise / Rest Day</Th>
                                  <Th>Started At</Th>
                                  <Th>Finished At</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {recentWorkouts.map((workout) => (
                                  <Tr key={workout._id}>
                                    <Td>{getDisplayTitle(workout.programId)}</Td>
                                    <Td>{typeof workout.weekIndex === 'number' ? workout.weekIndex + 1 : '-'}</Td>
                                    <Td>{typeof workout.dayIndex === 'number' ? workout.dayIndex + 1 : '-'}</Td>
                                    <Td>{workout.dayType || workout.sourceType || '-'}</Td>
                                    <Td>
                                      <Tag size='sm' colorScheme={workout.status === 'completed' ? 'green' : workout.status === 'skipped' ? 'orange' : 'blue'}>
                                        {workout.status || '-'}
                                      </Tag>
                                    </Td>
                                    <Td>{getExerciseOrRestTitle(workout)}</Td>
                                    <Td>{formatDate(workout.startedAt)}</Td>
                                    <Td>{formatDate(workout.finishedAt)}</Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                        ) : (
                          <Text color='gray.500'>No recent workouts found.</Text>
                        )}
                      </Box>
                    </VStack>
                  </TabPanel>

                  {/* ── Security ── */}
                  <TabPanel px={6} py={6}>
                    <VStack align='stretch' spacing={5}>
                      <TempPasswordPanel userId={getUserId(detailUser)} />
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            ) : (
              <Box p={6}><Text color='gray.500'>No user details found.</Text></Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Page>
  );
};

export default Users;

// changes
